import { Link, useParams, Navigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ManhwaCard from '@/components/feature/ManhwaCard';
import useSeo from '@/hooks/useSeo';
import { categories, manhwaList } from '@/mocks/manhwa';

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const item = manhwaList.find((m) => m.id === id);
  const cat = item ? categories.find((c) => c.id === item.category) : undefined;
  const related = item ? manhwaList.filter((m) => m.category === item.category && m.id !== item.id).slice(0, 4) : [];
  const more = related.length < 4
    ? manhwaList.filter((m) => m.category !== item?.category && m.id !== item?.id).slice(0, 4 - related.length)
    : [];
  const recs = [...related, ...more];

  useSeo({
    title: item
      ? `${item.title} 韩漫推荐详情 | 评分 ${item.score.toFixed(1)} - 好看的韩漫推荐`
      : '页面不存在 - 好看的韩漫推荐',
    description: item
      ? `${item.title} 是${cat?.name ?? ''}题材的人气韩漫，作者${item.author}。${item.summary} 来看看它为什么值得追更收藏。`
      : '您访问的页面不存在。',
    keywords: item ? `韩漫推荐,${item.title},${item.title}漫画,${cat?.name ?? ''}韩漫,韩漫书单` : '韩漫推荐',
    path: `/detail/${id ?? ''}`,
    type: 'article',
    jsonLd: item
      ? {
          '@context': 'https://schema.org',
          '@type': 'Book',
          name: item.title,
          author: { '@type': 'Person', name: item.author },
          about: cat?.name,
          description: item.summary,
          url: `${window.location.origin}/detail/${item.id}`,
          image: item.cover,
          aggregateRating: { '@type': 'AggregateRating', ratingValue: item.score, bestRating: 10, ratingCount: 1280 },
          breadcrumb: { '@type': 'BreadcrumbList', itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: `${window.location.origin}/` },
            { '@type': 'ListItem', position: 2, name: cat?.name ?? '书单', item: `${window.location.origin}/category/${item.category}` },
            { '@type': 'ListItem', position: 3, name: item.title, item: `${window.location.origin}/detail/${item.id}` },
          ]},
        }
      : [],
  });

  if (!item) return <Navigate to="/" replace />;

  return (
    <div className="bg-background-50">
      <Navbar />
      <main>
        <div className="relative overflow-hidden bg-foreground-950">
          <div className="absolute inset-0 opacity-20">
            <img src={item.cover} alt="" className="h-full w-full object-cover object-top" />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-background-950 via-background-950/80 to-background-950/40" />
          <div className="relative z-10 mx-auto max-w-7xl px-4 md:px-6 py-14 md:py-20">
            <nav aria-label="面包屑" className="flex items-center gap-1.5 text-sm text-background-300">
              <Link to="/" className="hover:text-accent-300 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line" />
              <Link to={`/category/${item.category}`} className="hover:text-accent-300 transition-colors">{cat?.name}书单</Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-background-50">{item.title}</span>
            </nav>

            <div className="mt-8 flex flex-col gap-8 md:flex-row">
              <div className="w-40 shrink-0 md:w-56">
                <img
                  src={item.cover}
                  alt={`${item.title} 封面`}
                  title={`${item.title} 韩漫推荐`}
                  className="aspect-[3/4] w-full rounded-xl object-cover object-top"
                />
              </div>
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-accent-500 px-3 py-1 text-sm font-bold text-background-950">
                    <i className="ri-star-fill" /> {item.score.toFixed(1)}
                  </span>
                  <span className="rounded-full bg-background-50/15 px-3 py-1 text-sm text-background-50 backdrop-blur">{item.status}</span>
                  <span className="rounded-full bg-background-50/15 px-3 py-1 text-sm text-background-50 backdrop-blur">{item.chapters} 话</span>
                </div>
                <h1 className="mt-4 font-heading text-3xl font-black text-background-50 md:text-5xl">{item.title}</h1>
                <p className="mt-2 text-base text-primary-300">作者 · {item.author}　｜　题材 · {cat?.name}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {item.tags.map((t) => (
                    <span key={t} className="rounded-full bg-background-50/15 px-3 py-1 text-sm text-background-100 backdrop-blur ring-1 ring-background-50/20">{t}</span>
                  ))}
                </div>
                <div className="mt-6 grid max-w-md grid-cols-3 gap-4">
                  <div className="rounded-xl bg-background-50/10 px-4 py-3 text-center">
                    <p className="text-xs text-background-300">评分</p>
                    <p className="mt-1 font-heading text-xl font-bold text-accent-300">{item.score.toFixed(1)}</p>
                  </div>
                  <div className="rounded-xl bg-background-50/10 px-4 py-3 text-center">
                    <p className="text-xs text-background-300">状态</p>
                    <p className="mt-1 font-heading text-xl font-bold text-background-50">{item.status}</p>
                  </div>
                  <div className="rounded-xl bg-background-50/10 px-4 py-3 text-center">
                    <p className="text-xs text-background-300">话数</p>
                    <p className="mt-1 font-heading text-xl font-bold text-background-50">{item.chapters}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16">
          <article className="max-w-3xl">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">作品简介</h2>
            <p className="mt-4 text-base leading-8 text-foreground-700">{item.summary}</p>
            <p className="mt-4 text-base leading-8 text-foreground-700">
              这部韩漫凭借扎实的叙事与出色的画工，在 {cat?.name} 题材里积累了稳定的口碑。无论是第一次接触这个题材的新读者，还是想扩充收藏的老读者，都值得把它放进书单。
            </p>
            <div className="mt-6 rounded-2xl border border-background-200 bg-background-100 p-5">
              <h3 className="font-heading text-base font-semibold text-foreground-950">为什么推荐这部韩漫？</h3>
              <ul className="mt-3 space-y-2 text-sm leading-6 text-foreground-700">
                <li className="flex gap-2"><i className="ri-checkbox-circle-line mt-0.5 text-primary-600" /> 高分口碑：评分 {item.score.toFixed(1)}，读者认可度高，追更不踩雷。</li>
                <li className="flex gap-2"><i className="ri-checkbox-circle-line mt-0.5 text-primary-600" /> 题材特色：紧扣 {cat?.name} 题材核心看点，剧情与画工双在线。</li>
                <li className="flex gap-2"><i className="ri-checkbox-circle-line mt-0.5 text-primary-600" /> 追更友好：已更新 {item.chapters} 话，内容充实，可以放心入坑。</li>
              </ul>
            </div>
          </article>

          <section className="mt-14">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">同类韩漫推荐</h2>
            <p className="mt-2 text-sm text-foreground-600">喜欢这部作品？再看看下面这些同类佳作。</p>
            <div className="mt-6 grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4">
              {recs.map((m) => (
                <ManhwaCard key={m.id} item={m} />
              ))}
            </div>
          </section>
        </div>
      </main>
      <Footer />
      <button
        type="button"
        aria-label="返回顶部"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-6 right-6 z-40 flex h-11 w-11 items-center justify-center rounded-full bg-primary-500 text-background-50 transition-colors hover:bg-primary-600"
      >
        <i className="ri-arrow-up-line text-lg" />
      </button>
    </div>
  );
}